/**
 * Know Sure Thing (KST)
 *
 * Martin Pring's momentum oscillator based on weighted sum of
 * four different ROC periods, each smoothed with SMA.
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA } from "./sma";

export const kstIndicator: IndicatorDefinition = {
  id: "kst",
  name: "Know Sure Thing",
  shortName: "KST",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "roc1", label: "ROC 1", type: "number", default: 10, min: 1, max: 100 },
    { key: "roc2", label: "ROC 2", type: "number", default: 15, min: 1, max: 100 },
    { key: "roc3", label: "ROC 3", type: "number", default: 20, min: 1, max: 100 },
    { key: "roc4", label: "ROC 4", type: "number", default: 30, min: 1, max: 200 },
    { key: "signal", label: "Signal", type: "number", default: 9, min: 1, max: 50 },
  ],
  plots: [
    { key: "kst", label: "KST", color: "#2196F3", style: "line", lineWidth: 2, priceScaleId: "kst" },
    { key: "signal", label: "Signal", color: "#FF5722", style: "line", lineWidth: 1, priceScaleId: "kst" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { roc1, roc2, roc3, roc4, signal } = params;
    const closes = candles.map(c => c.close);

    // ROC helper
    const roc = (arr: number[], p: number): number[] => {
      const r: number[] = new Array(p).fill(NaN);
      for (let i = p; i < arr.length; i++) {
        r.push(arr[i - p] !== 0 ? ((arr[i] - arr[i - p]) / arr[i - p]) * 100 : 0);
      }
      return r;
    };

    const r1 = computeSMA(roc(closes, roc1), 10);
    const r2 = computeSMA(roc(closes, roc2), 10);
    const r3 = computeSMA(roc(closes, roc3), 10);
    const r4 = computeSMA(roc(closes, roc4), 15);

    const kstValues: number[] = [];
    for (let i = 0; i < candles.length; i++) {
      if (isNaN(r1[i]) || isNaN(r2[i]) || isNaN(r3[i]) || isNaN(r4[i])) {
        kstValues.push(NaN);
      } else {
        kstValues.push(r1[i] * 1 + r2[i] * 2 + r3[i] * 3 + r4[i] * 4);
      }
    }

    const sigValues = computeSMA(kstValues.filter(v => !isNaN(v)), signal);

    const kstData: { time: number; value: number }[] = [];
    const sigData: { time: number; value: number }[] = [];

    let sigIdx = 0;
    for (let i = 0; i < kstValues.length; i++) {
      if (!isNaN(kstValues[i])) {
        kstData.push({ time: candles[i].time, value: kstValues[i] });
        if (sigIdx < sigValues.length && !isNaN(sigValues[sigIdx])) {
          sigData.push({ time: candles[i].time, value: sigValues[sigIdx] });
        }
        sigIdx++;
      }
    }

    return [{ key: "kst", data: kstData }, { key: "signal", data: sigData }];
  },
};
